import { nextTick, watch } from 'vue'
import type { Ref } from 'vue'

const LOCATE_HIGHLIGHT_DURATION_MS = 3200

export type OrgLocateHighlightQueryKey =
  | 'highlightDirection'
  | 'highlightDomaine'
  | 'highlightAgence'
  | 'highlightEquipe'
  | 'highlightSection'

type UseOrgLocateHighlightParams = {
  /** When false, highlight watchers do not run. */
  isReady: Ref<boolean>
  /** Scroll block for scrollIntoView (default: 'nearest'). */
  scrollBlock?: ScrollLogicalPosition
  triggers: Array<{
    queryKey: OrgLocateHighlightQueryKey
    /** Return a CSS selector when this highlight should run, or null to skip. */
    resolveSelector: () => string | null
  }>
}

export function useOrgLocateHighlight(params: UseOrgLocateHighlightParams) {
  const route = useRoute()
  const scrollBlock = params.scrollBlock ?? 'nearest'

  function triggerLocateHighlight(
    targetSelector: string,
    queryKeyToClear: OrgLocateHighlightQueryKey,
  ) {
    nextTick(() => {
      const targetElement = document.querySelector(targetSelector) as HTMLElement | null
      if (!targetElement) return
      targetElement.scrollIntoView({ behavior: 'smooth', block: scrollBlock, inline: 'nearest' })
      targetElement.classList.add('org-locate-highlight')
      window.setTimeout(() => {
        targetElement.classList.remove('org-locate-highlight')
        const { [queryKeyToClear]: _removedHighlightKey, ...queryWithoutHighlight } = route.query
        void _removedHighlightKey
        void navigateTo({ path: route.path, query: queryWithoutHighlight }, { replace: true })
      }, LOCATE_HIGHLIGHT_DURATION_MS)
    })
  }

  watch(
    () => [
      params.isReady.value,
      ...params.triggers.map(({ queryKey }) => route.query[queryKey] as string | undefined),
    ] as const,
    () => {
      if (!params.isReady.value) return
      for (const trigger of params.triggers) {
        const selector = trigger.resolveSelector()
        if (selector) {
          triggerLocateHighlight(selector, trigger.queryKey)
          return
        }
      }
    },
    { immediate: true, flush: 'post' },
  )
}
